import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { ACCESS_DENIED } from "../lib/auth";
import { useGrpcClient } from "../lib/grpc";
import { GetPropertyChildrenRequest } from "../gen/witbooking/backoffice/property/v1/property_api_pb";
import { useKeycloakAuthz } from "./keycloak-provider";
import { PropertyAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { useProperty } from "./property-provider";

export interface IPropertyChainData {
  isChain: boolean;
  children: string[];
}

export interface ChainDataProviderProps {
  propertyTicker: string;
  children: ReactNode | ((value: ChainDataValue) => ReactNode);
}

type ChainDataValue = IPropertyChainData | undefined | null;
export const ChainDataContext = createContext<ChainDataValue>(undefined);

export function ChainDataProvider({ propertyTicker, children }: ChainDataProviderProps) {
  const [data, setData] = useState<ChainDataValue>(undefined);
  const keycloakAuthz = useKeycloakAuthz();
  const grpcClient = useGrpcClient(PropertyAPIPromiseClient);
  const property = useProperty();

  useEffect(() => {
    async function load() {
      try {
        const token = property.token;
        const propertyChildren = await grpcClient.getPropertyChildren(
          new GetPropertyChildrenRequest(),
          { Authorization: `Bearer ${token}` }
        );
        const children = propertyChildren.toString().length
          ? propertyChildren.toString().split(",")
          : [];
        setData({
          isChain: !!children.length,
          children: children,
        });
      } catch (e) {
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
        } else {
          console.error("Error retrieving rpt", e);
        }
      }
    }

    load();
  }, [grpcClient, keycloakAuthz, property.token, propertyTicker]);

  return (
    <>
      {data === undefined}
      {data !== undefined && (
        <ChainDataContext.Provider value={data}>
          {typeof children === "function" ? children(data) : children}
        </ChainDataContext.Provider>
      )}
    </>
  );
}

export function useChainData(): ChainDataValue {
  return useContext(ChainDataContext);
}
